import React, { useRef, useState, useCallback, useEffect } from "react";
import SelectBox from 'devextreme-react/select-box';
import DataGrid, {
    Column,
    KeyboardNavigation,
    FilterRow,
    Paging,
    Pager,
    SearchPanel,
    Toolbar,
    Lookup,
    Item,
    Selection,
    Summary,
    GroupItem,
    GroupPanel,
    Grouping,
    TotalItem,
    Export,
    ColumnFixing
} from 'devextreme-react/data-grid';
import { useNavigate } from "react-router-dom";
import { ROUTE_CREATE_EDIT_POST } from "../../routes/Routes";
import { allowedPageSizes, pageFormats } from "../../components/GlobalDataGridConfigurations";
import { Button } from "devextreme-react";
import ExportGrid from "../../components/ExportGrid";

export default function DataGridPost({ postExhibit, postExhibitFormData, feetypes, postStatuses, editing, postDataRef, isCostEstimate }) {
    const navigate = useNavigate();
    const [groupColumn, setGroupColumn] = useState('PostId');
    const [hiddenPosts, setHiddenPosts] = useState([]);
    const [isShowingHidden, setIsShowingHidden] = useState(false);
    const [filteredPosts, setFilteredPosts] = useState(postExhibitFormData?.Posts || []);

    const countLabel = { 'aria-label': 'Totals' };
    const groupingValues = [
        {
            value: 'PostId',
            text: 'Total By Post',
        },
        {
            value: 'Building.Name',
            text: 'Total by Building',
        },
        {
            value: 'CustomerName',
            text: 'Total by Customer',
        },
        {
            value: 'CLIN',
            text: 'Total by CLIN',
        },
        {
            value: 'FeeTypeId',
            text: 'Total by FeeType',
        },
    ];

    const navigateToNewPostForm = () => {
        navigate(ROUTE_CREATE_EDIT_POST.withSlash, {
            state: {
                postExhibit: postExhibit,
                postExhibitFormData: postExhibitFormData,
                isNewPostExhibit: true,
                isCostEstimate: isCostEstimate
            },
        });
    }

    const navigateToEditPostForm = async (post) => {

        navigate(ROUTE_CREATE_EDIT_POST.withSlash, {
            state: {
                post: post,
                postExhibit: postExhibit,
                postExhibitFormData: postExhibitFormData,
                isCostEstimate: isCostEstimate
            }
        });

    }

    const toggleGroupColumn = useCallback((e) => {
        const newGrouping = e.value;
        setGroupColumn(newGrouping);

        if (postDataRef.current) {
            postDataRef.current.instance().clearGrouping();

            if (newGrouping !== 'PostId') {
                postDataRef.current.instance().columnOption(newGrouping, 'groupIndex', 0);
            }
        }
    }, []);

    const toggleHideSelected = () => {
        if (isShowingHidden) {
            setHiddenPosts([]);
            setIsShowingHidden(false);
        } else {
            const selectedPosts = postDataRef.current?.instance().getSelectedRowKeys();
            setHiddenPosts(selectedPosts);
            setIsShowingHidden(true);
        }
    };

    useEffect(() => {
        const hiddenPostIdsArray = hiddenPosts.map(post => Number(post.PostId));

        const updatedPosts = isShowingHidden
            ? postExhibitFormData?.Posts.filter(post => !hiddenPostIdsArray.includes(Number(post.PostId)))
            : postExhibitFormData?.Posts;

        setFilteredPosts(updatedPosts);
    }, [hiddenPosts, isShowingHidden, postExhibitFormData?.Posts]);

    // Calculate allocation hours and costs for each post and customer
    const processedPosts = filteredPosts?.flatMap(post => {
        // For posts with allocations
        if (post.PostAllocations && post.PostAllocations.length > 0) {
            return post.PostAllocations.map(allocation => {
                // Calculate allocation rate based on building and occupancy agreement
                const calculateAllocationRate = () => {
                    if (post.Building?.AnsiRentSqFt && allocation.OccupancyAgreement?.TotalRentSqFt) {
                        return Number(
                            ((allocation.OccupancyAgreement.TotalRentSqFt / post.Building.AnsiRentSqFt) * 100).toFixed(2)
                        );
                    }
                    return allocation.AllocationRate || 0;
                };

                const allocationRate = calculateAllocationRate();
                const utilizationRate = post.Utilization || 100;

                // Calculate hours and costs for this allocation
                const allocationHours = post.TotalServiceHours * (utilizationRate / 100) * (allocationRate / 100);
                const allocationCosts = post.HourlyRate * allocationHours;

                return {
                    ...post,
                    CustomerName: allocation.CustomerName,
                    CustomerNumber: allocation.CustomerNumber,
                    AgencyName: allocation.AgencyName,
                    PostAllocationId: allocation.PostAllocationId,
                    PostAllocationNumber: allocation.PostAllocationNumber,
                    AllocationRate: allocationRate,
                    AllocationHours: allocationHours.toFixed(2),
                    AllocationCosts: allocationCosts.toFixed(2),
                    ProjectCode: allocation.ProjectCode
                };
            });
        } else {
            // For posts without allocations, use total values
            return [{
                ...post,
                CustomerName: null,
                CustomerNumber: null,
                AgencyName: null,
                AllocationRate: 100,
                AllocationHours: post.TotalServiceHours,
                AllocationCosts: post.TotalServiceCosts
            }];
        }
    });

    // Group posts by PostId to show a single edit button per post
    const renderEditLinkCell = (cellData) => {
        const isFirstAppearance = processedPosts.findIndex(p => p.PostId === cellData.value) ===
            processedPosts.findIndex(p => p.PostId === cellData.value &&
                p.PostAllocationId === cellData.data.PostAllocationId);

        if (isFirstAppearance) {
            const originalPost = postExhibitFormData.Posts.find(
                p => p.PostId === cellData.value || p.TempPostId === cellData.value
            );

            return (
                <a
                    href="#"
                    style={{fontWeight: 'bold', color: '#005288'}}
                    onClick={(e) => {
                        e.preventDefault();
                        navigateToEditPostForm(originalPost);
                    }}
                >
                    Edit: {cellData.value}
                </a>
            );
        } else {
            return <span>{cellData.value}</span>;
        }
    };

    // Calculate the true total costs without duplication
    const calculateTotalServiceCosts = () => {
        if (!filteredPosts || filteredPosts.length === 0) return 0;

        // Get unique posts and sum their costs only once
        const uniqueCosts = filteredPosts.reduce((total, post) => {
            const baseCost = parseFloat(post.TotalServiceCosts);
            if (!isNaN(baseCost)) {
                return total + (postExhibitFormData?.InflationRate ? baseCost * 1.1 : baseCost);
            }
            return total;
        }, 0);

        return uniqueCosts;
    };

    const totalServiceCosts = calculateTotalServiceCosts();

    const handleExport = (e) => {
        ExportGrid(e, `Post_Exhibit_${postExhibitFormData?.PostExhibitId}`)
    }

    return (
        <DataGrid
            id="postExhibit_postDataGrid"
            ref={postDataRef}
            columnAutoWidth={true}
            columnRenderingMode="standard"
            dataSource={processedPosts}
            showBorders={true}
            showRowLines={true}
            rowAlternationEnabled={true}
            onExporting={(e) => handleExport(e)}
            key='PostAllocationId'>
            <FilterRow visible={true} />
            <KeyboardNavigation enabled={true} />
            <Paging defaultPageSize={10} />
            <Pager
                showPageSizeSelector={true}
                allowedPageSizes={allowedPageSizes}
            />
            <SearchPanel
                visible={true}
                highlightCaseSensitive={true}
                searchVisibleColumnsOnly={false}
            />
            <Selection mode={"multiple"} showCheckBoxesMode="always" selectAllMode="page" />
            <ColumnFixing enabled={true} />
            <GroupPanel visible={true} />
            <Toolbar>
                <Item location="before">
                    <Button
                        text={isShowingHidden ? "Show All" : "Hide Selected"}
                        onClick={toggleHideSelected}
                    />
                </Item>
  {/*               <Item location="before">
                    <SelectBox
                        width="225"
                        items={groupingValues}
                        displayExpr="text"
                        inputAttr={countLabel}
                        valueExpr="value"
                        value={groupColumn}
                        onValueChanged={toggleGroupColumn} />
                </Item> */}
                <Item
                    name="searchPanel"
                    locateInMenu="after"
                />
                <Item name="exportButton" location="after" />
                <Item
                    widget="dxButton"
                    location="after"
                    options={{
                        icon: 'add',
                        text: 'New Post',
                        onClick: () => {
                            navigateToNewPostForm();
                        }
                    }}
                    visible={editing}
                />
            </Toolbar>
            {editing ?
                (
                    <Column
                        dataField="PostId"
                        caption="Post ID"
                        cellRender={renderEditLinkCell}
                        fixed={true}
                        fixedPosition="left"
                    />
                ) :
                <Column
                    dataField="PostId"
                    caption="Post ID"
                    fixed={true}
                    fixedPosition="left"
                />
            }
            {
                /* Default Fields:
                    Post ID, CLIN, Building Number, Post Number, Agency, Address, City, 
                    State, Screener, Post Type, Post Location, Fitness Security
                */
            }
            <Column
                dataField="CLIN"
                caption="CLIN"
                fixed={true}
                fixedPosition="sticky"
            />
            <Column
                dataField="Building.SequenceNumber"
                caption="Building Number"
                fixed={true}
                fixedPosition="sticky"
            />
            <Column
                dataField="PostNumber"
                caption="Post Number"
                fixed={true}
                fixedPosition="sticky"
            />
            <Column
                dataField="AgencyName"
                caption="Agency Name"
                fixed={true}
                fixedPosition="sticky"
            />
            <Column
                dataField="BuildingAddress.Address1"
                caption="Address"
            />
            <Column
                dataField="BuildingAddress.CityName"
                caption="City"
            />
            <Column
                dataField="BuildingAddress.State"
                caption="State"
            />
            <Column
                dataField="Screener.Name"
                caption="Screener"
            />
            <Column
                dataField="PrimaryPostType.Name"
                caption="Post Type"
            />
            <Column
                dataField="PostLocation"
                caption="Post Location"
            />
            <Column
                dataField="SecurityLevel.Name"
                caption="Fitness Security"
            />
            <Column
                dataField="BuildingAddress.ZipCode"
                caption="Zip Code"
            />
            <Column
                dataField="PostAllocationNumber"
                caption="Allocation Number"
            />
            <Column
                dataField="Building.RegionNumber"
                caption="Region"
            />
            <Column
                dataField="PostStatusId"
                caption="Status"
            >
                <Lookup
                    dataSource={postStatuses}
                    displayExpr='Name'
                    valueExpr='PostStatusId'
                />
            </Column>
            <Column
                dataField="FeeTypeId"
                caption="Fee Type"
            >
                <Lookup
                    dataSource={feetypes}
                    displayExpr='FeeTypeName'
                    valueExpr='FeeTypeId'
                />
            </Column>
            <Column
                dataField="Building.Name"
                caption="Building Name"
            />
            <Column
                dataField="CustomerName"
                caption="Customer Name"
                // sortOrder="asc"
                // groupIndex={0}
            />
            <Column
                dataField="CustomerNumber"
                caption="Customer Number"
            />
            <Column
                dataField="StartDate"
                caption="Start Date"
            />
            <Column
                dataField="EndDate"
                caption="End Date"
            />
            <Column
                dataField="Utilization"
                caption="Utilization Rate (%)"
            />
            <Column
                dataField="AllocationRate"
                caption="Allocation Rate (%)"
            />
            <Column
                dataField="HourlyRate"
                caption="Hourly Rate"
            />
            <Column
                dataField="ProjectCode"
                caption="Project Code"
            />
            <Column
                dataField="FundingSource"
                caption="Funding Source"
            />
            <Column
                dataField="FundingStatus.Value"
                caption="Funding Status"
            />
            <Column
                dataField="ShortFundedDate"
                caption="Short Funded Date"
            />
            <Column
                dataField="Comment"
                caption="Comment"
                cellRender={(cellData) => (
                    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {cellData.value}
                    </div>)
                }
            />
            <Column
                dataField="TotalServiceHours"
                caption="Total Post Hours"

            />
            <Column
                dataField="AllocationHours"
                caption="Allocation Hours"
                format="#.##"
            />
            <Column
                dataField="TotalServiceCosts"
                format="currency"
                caption="Total Post Costs"
                calculateCellValue={(rowData) => {
                    const baseCost = parseFloat(rowData.TotalServiceCosts);
                    if (!isNaN(baseCost)) {
                        return postExhibitFormData?.InflationRate ? baseCost * 1.1 : baseCost;
                    }
                    return null;
                }}
            />
            <Column
                dataField="AllocationCosts"
                format="currency"
                caption="Total Costs"
                calculateCellValue={(rowData) => {
                    const baseCost = parseFloat(rowData.AllocationCosts);
                    if (!isNaN(baseCost)) {
                        return postExhibitFormData?.InflationRate ? baseCost * 1.1 : baseCost;
                    }
                    return null;
                }}
            />
            <Summary calculateCustomSummary={(options) => {
                if (options.name === 'TotalServiceCostsCustom' && options.summaryProcess === 'finalize') {
                    options.totalValue = totalServiceCosts;
                }
            }}
            >
                <GroupItem
                    column="AllocationCosts"
                    summaryType="sum"
                    valueFormat="currency"
                    displayFormat="Allocation Total: {0}"
                    showInGroupFooter={true}
                    calculateSummaryValue={(data) => {
                        const baseCost = parseFloat(data.AllocationCosts);
                        return !isNaN(baseCost)
                            ? (postExhibitFormData?.InflationRate ? baseCost * 1.1 : baseCost)
                            : null;
                    }}
                />
                <GroupItem
                    column="TotalServiceCosts"
                    summaryType="sum"
                    valueFormat="currency"
                    displayFormat="Subtotal: {0}"
                    showInGroupFooter={true}
                    calculateSummaryValue={(data) => {
                        const baseCost = parseFloat(data.TotalServiceCosts);
                        return !isNaN(baseCost)
                            ? (postExhibitFormData?.InflationRate ? baseCost * 1.1 : baseCost)
                            : null;
                    }}
                />
                <TotalItem
                    column="TotalServiceCosts"
                    summaryType="custom"
                    valueFormat="currency"
                    displayFormat="Total: {0}"
                    name="TotalServiceCostsCustom"
                />
            </Summary>
            <Export
                enabled={true}
                formats={pageFormats}
            />
        </DataGrid>
    )
}